import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { FiltersButton } from '@/components/common/FiltersButton'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { reviewQueueApi, type ReviewQueueRow, type ReviewQueueState } from '@/api/exercises'
import { usersApi } from '@/api/users'
import type { User } from '@/types'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY } from '@/utils/date'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

/** Источник работы; `all` — «не фильтровать», в API такой параметр не уходит */
type QueueContext = 'all' | 'lesson' | 'homework'

// Самопрактики в очереди нет: тренировка учителя не дёргает — по той же
// причине, по которой не шлёт уведомление
const CONTEXTS: QueueContext[] = ['all', 'lesson', 'homework']

/**
 * Срезы очереди по состоянию работы. `pending` — ждут учителя, `checked` — всё
 * проверенное (автобалл робота, ручная оценка учителя, проверенный текстовый
 * блок домашки), `all` — и то, и другое.
 */
const STATES: ReviewQueueState[] = ['pending', 'checked', 'all']

/** Срез по умолчанию — тот же, что применяет сервер, если `state` не прислали */
const DEFAULT_STATE: ReviewQueueState = 'pending'

/** Строк в скелетоне: столько же, сколько обычно влезает на экран без прокрутки */
const SKELETON_ROWS = [1, 2, 3, 4, 5]

/**
 * Где помним последний срез очереди.
 *
 * Сам срез живёт в адресе, но страницы проверки возвращают учителя на голый
 * `/exercises/review`: после переоценки он оказывался в «ждут проверки» на
 * первой странице, хотя работал в «проверенных» на третьей — и на потоке из
 * десятка работ фильтрами пользоваться было нельзя. Session, а не local:
 * запоминаем на время работы, а не навсегда.
 */
const QUEUE_PARAMS_KEY = 'exercises.review.queue'

/** Разбор параметров адреса: чужое значение в ссылке не должно ломать страницу */
const parseContext = (value: string | null): QueueContext =>
  CONTEXTS.includes(value as QueueContext) ? (value as QueueContext) : 'all'

const parseState = (value: string | null): ReviewQueueState =>
  STATES.includes(value as ReviewQueueState) ? (value as ReviewQueueState) : DEFAULT_STATE

/** В адресе страницы человеческие, с единицы; MUI считает их с нуля */
const parsePage = (value: string | null): number => {
  const parsed = Number.parseInt(value ?? '', 10)

  return Number.isFinite(parsed) && parsed > 1 ? parsed - 1 : 0
}

/** id ученика из адреса; мусор и чужие значения отсекает сервер, здесь — только формат */
const parseStudentId = (value: string | null): number | null => {
  const parsed = Number.parseInt(value ?? '', 10)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * Дата фильтра — строго `YYYY-MM-DD`. Форму проверяем регуляркой, а не разбором
 * dayjs: строгий разбор по формату требует плагина, которого в проекте нет, и
 * `dayjs('01.03.2026')` тихо согласился бы. Пустую строку в API не шлём —
 * для сервера это не «нет фильтра», а неверная дата.
 */
const parseDate = (value: string | null): string =>
  value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).isValid() ? value : ''

interface QueueParams {
  context: QueueContext
  state: ReviewQueueState
  studentId: number | null
  submittedFrom: string
  submittedTo: string
  page: number
}

/**
 * Сборка адреса из состояния фильтров. Одна функция и для записи по клику, и
 * для сверки с эхом сервера: сравниваем строки адресов, а порядок ключей в них
 * должен совпадать, иначе сверка будет вечно видеть расхождение.
 */
const buildQueueParams = (values: QueueParams): URLSearchParams => {
  const params = new URLSearchParams()
  params.set('context', values.context)
  params.set('state', values.state)
  // Пустые фильтры в адрес не пишем: ссылка на срез должна читаться глазами
  if (values.studentId !== null) params.set('student_id', String(values.studentId))
  if (values.submittedFrom !== '') params.set('submitted_from', values.submittedFrom)
  if (values.submittedTo !== '') params.set('submitted_to', values.submittedTo)
  params.set('page', String(values.page + 1))

  return params
}

/** Чтение и запись памяти среза: приватный режим браузера кидает исключение */
const readSavedParams = (): string | null => {
  try {
    return window.sessionStorage.getItem(QUEUE_PARAMS_KEY)
  } catch {
    return null
  }
}

const saveParams = (value: string): void => {
  try {
    window.sessionStorage.setItem(QUEUE_PARAMS_KEY, value)
  } catch {
    // Память среза — удобство, а не условие работы страницы
  }
}

/** Подпись ученика в фильтре: тем же порядком полей, что и в списках учеников */
const studentOptionLabel = (user: User): string =>
  user.display_name?.trim() ||
  [user.surname, user.first_name].filter((part): part is string => Boolean(part?.trim())).join(' ').trim() ||
  user.email ||
  `#${user.id}`

/** Фильтры, которые сервер подтвердил эхом в `meta` */
interface AppliedFilters {
  state: ReviewQueueState
  studentId: number | null
  submittedFrom: string
  submittedTo: string
}

/**
 * Единый раздел «На проверке»: работы с урока, самопрактика и пункты домашки.
 *
 * Раньше это были два пункта меню с разной логикой, и ни один не отвечал на
 * главный вопрос — сколько работ висит всего. Порядок общий: по времени
 * ожидания, самая давняя сверху, иначе сдавший первым ждёт дольше всех.
 */
export function ExerciseReviewQueuePage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  // Фильтры и страница живут в адресе, а не в стейте: учитель уходит отсюда в
  // проверку и возвращается обратно, и стейт страницы при этом сбрасывался —
  // каждая проверенная работа выкидывала его в начало очереди
  const [searchParams, setSearchParams] = useSearchParams()

  const [rows, setRows] = useState<ReviewQueueRow[]>([])
  const [total, setTotal] = useState(0)
  const [perPage, setPerPage] = useState(25)
  const [students, setStudents] = useState<User[]>([])
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null)
  const [applied, setApplied] = useState<AppliedFilters | null>(null)

  // Голый адрес разбираем сразу по памяти сессии, не дожидаясь эффекта ниже:
  // иначе первый рендер успевал сходить в API за срезом по умолчанию и мигнуть
  // учителю чужим списком
  const effectiveParams = useMemo(
    () => (searchParams.toString() === '' ? new URLSearchParams(readSavedParams() ?? '') : searchParams),
    [searchParams],
  )

  const context = parseContext(effectiveParams.get('context'))
  const queueState = parseState(effectiveParams.get('state'))
  const studentId = parseStudentId(effectiveParams.get('student_id'))
  const submittedFrom = parseDate(effectiveParams.get('submitted_from'))
  const submittedTo = parseDate(effectiveParams.get('submitted_to'))
  const page = parsePage(effectiveParams.get('page'))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Запись выбранных фильтров в адрес.
   *
   * `replace`: смена фильтра — не переход по истории, иначе кнопка «назад»
   * браузера отматывала бы фильтры по одному вместо возврата к работе. Пишем
   * весь набор сразу — по такому адресу срез восстанавливается целиком, и
   * ссылкой на него можно поделиться.
   */
  const applyParams = useCallback(
    (next: Partial<QueueParams>) => {
      const params = buildQueueParams({
        context: next.context ?? context,
        state: next.state ?? queueState,
        // Здесь `undefined` — «не трогать», а `null` — «снять фильтр»,
        // поэтому `??` не годится: он съел бы снятие
        studentId: next.studentId !== undefined ? next.studentId : studentId,
        submittedFrom: next.submittedFrom ?? submittedFrom,
        submittedTo: next.submittedTo ?? submittedTo,
        page: next.page ?? page,
      })

      setSearchParams(params, { replace: true })
    },
    [context, page, queueState, setSearchParams, studentId, submittedFrom, submittedTo],
  )

  // Адрес — источник правды, но со страниц проверки учитель возвращается на
  // голый `/exercises/review`. Пустой адрес достраиваем из памяти сессии, а
  // непустой в неё же и записываем: так возврат после переоценки попадает в тот
  // самый срез и на ту самую страницу, где учитель работал
  useEffect(() => {
    const current = searchParams.toString()

    if (current !== '') {
      saveParams(current)

      return
    }

    const saved = readSavedParams()

    if (saved !== null && saved !== '') {
      setSearchParams(new URLSearchParams(saved), { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Ученики для фильтра — один раз на вход в раздел, а не на каждое
  // перелистывание: список не зависит ни от среза, ни от страницы очереди
  useEffect(() => {
    let cancelled = false

    usersApi
      .listAllStudents()
      .then((list) => {
        if (!cancelled) setStudents(list)
      })
      .catch(() => {
        // Не загрузился список для фильтра — очередь всё равно работает,
        // ошибку этого запроса поверх неё показывать незачем
      })

    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Текст ошибки очереди. 422 сюда приходит только из-за фильтров: чужой
   * ученик в адресе или перевёрнутый диапазон дат. Серверное сообщение написано
   * про поля запроса, поэтому подменяем его человеческим — учителю нужно знать,
   * что поправить, а не как называется параметр.
   */
  const queueErrorMessage = useCallback(
    (err: unknown): string => {
      const response =
        typeof err === 'object' && err !== null
          ? (err as { response?: { status?: number; data?: { errors?: Record<string, unknown> } } }).response
          : undefined

      if (response?.status === 422) {
        const errors = response.data?.errors ?? {}

        if ('student_id' in errors) return t('exercises.review.errors.foreignStudent')
        if ('submitted_from' in errors || 'submitted_to' in errors) return t('exercises.review.errors.dateRange')

        return t('exercises.review.errors.badFilters')
      }

      return displayErrorMessage(getErrorMessage(err), t('common.error'))
    },
    [t],
  )

  // Номер запроса: фильтры переключают быстро, и без него победителем в списке
  // оказывался тот ответ, который пришёл последним, а не тот, который
  // соответствует выбранному фильтру
  const requestRef = useRef(0)

  const load = useCallback(async () => {
    const request = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      const result = await reviewQueueApi.list({
        context: context === 'all' ? undefined : context,
        state: queueState,
        studentId,
        submittedFrom: submittedFrom || null,
        submittedTo: submittedTo || null,
        page: page + 1,
        perPage,
      })

      if (request !== requestRef.current) return

      setRows(result.rows)
      setTotal(result.total)
      setApplied({
        state: result.state,
        studentId: result.studentId,
        submittedFrom: result.submittedFrom ?? '',
        submittedTo: result.submittedTo ?? '',
      })
    } catch (err) {
      if (request !== requestRef.current) return

      // Список чистим: строки от прошлого запроса под сообщением об ошибке
      // читались бы как результат отклонённых фильтров
      setRows([])
      setTotal(0)
      setError(queueErrorMessage(err))
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }, [context, page, perPage, queueErrorMessage, queueState, studentId, submittedFrom, submittedTo])

  useEffect(() => {
    void load()
  }, [load])

  // Эхо `meta`: сервер отвечает тем, что реально применил, — по нему и
  // приводим адрес к правде. Нужно на возврате из проверки и после
  // перезагрузки: в памяти сессии мог остаться адрес прошлой версии, без
  // `state`, и подписи панели разошлись бы с выдачей. Сверяем только по
  // успешному ответу и вне загрузки — иначе эхо предыдущего запроса затирало бы
  // фильтр, который учитель только что выбрал.
  useEffect(() => {
    if (loading || error !== null || applied === null) return

    const normalized = buildQueueParams({
      // `context` сервер не повторяет: он не фильтр запроса, а его адресат
      context,
      state: applied.state,
      studentId: applied.studentId,
      submittedFrom: applied.submittedFrom,
      submittedTo: applied.submittedTo,
      page,
    })

    if (normalized.toString() !== searchParams.toString()) {
      setSearchParams(normalized, { replace: true })
    }
  }, [applied, context, error, loading, page, searchParams, setSearchParams])

  /** Последняя существующая страница выдачи по `total` с сервера */
  const lastPage = useMemo(
    () => Math.max(0, Math.ceil(total / Math.max(perPage, 1)) - 1),
    [perPage, total],
  )

  // Страницы за пределами выдачи возвращаем на последнюю существующую.
  // Номер страницы восстанавливается из памяти сессии на каждый возврат на
  // голый `/exercises/review`, а работ к тому времени осталось меньше: учитель
  // проверил единственную работу второй страницы и возвращался на пустую.
  // Сама пагинация теперь рисуется и над пустым списком (см. ниже), так что
  // выход есть в любом случае, но здесь возврат происходит сам.
  useEffect(() => {
    if (loading || error !== null || applied === null) return

    if (page > lastPage) applyParams({ page: lastPage })
  }, [applied, applyParams, error, lastPage, loading, page])

  /**
   * Число применённых фильтров на кнопке. Считаем то, что реально сузило
   * выдачу и при этом спрятано в панели: развёрнутый срез виден и так, а вот
   * свёрнутый молча режет список, и без счётчика учитель ищет пропавшие работы.
   * Срез по умолчанию (`pending`) не считаем — это состав очереди, а не фильтр.
   */
  const appliedFiltersCount = useMemo(() => {
    let count = 0

    if (context !== 'all') count += 1
    if (queueState !== DEFAULT_STATE) count += 1
    if (studentId !== null) count += 1
    // Диапазон — один фильтр: половинки «с» и «до» учитель задаёт одним полем
    if (submittedFrom !== '' || submittedTo !== '') count += 1

    return count
  }, [context, queueState, studentId, submittedFrom, submittedTo])

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === studentId) ?? null,
    [studentId, students],
  )

  const openDateRange = (event: MouseEvent<HTMLElement>) => {
    setDateAnchorEl(event.currentTarget)
  }

  const closeDateRange = () => {
    setDateAnchorEl(null)
  }

  const dateCalendarValue: Dayjs | null = submittedTo
    ? dayjs(submittedTo)
    : submittedFrom
      ? dayjs(submittedFrom)
      : null

  const dateRangeLabel = useMemo(() => {
    if (submittedFrom !== '' && submittedTo !== '') {
      return `${dayjs(submittedFrom).format('DD.MM.YYYY')} - ${dayjs(submittedTo).format('DD.MM.YYYY')}`
    }
    if (submittedFrom !== '') {
      return t('exercises.review.periodFrom', { date: dayjs(submittedFrom).format('DD.MM.YYYY') })
    }
    if (submittedTo !== '') {
      return t('exercises.review.periodTo', { date: dayjs(submittedTo).format('DD.MM.YYYY') })
    }

    return ''
  }, [submittedFrom, submittedTo, t])

  /**
   * Клик по календарю: первый выбирает начало, второй — конец. Обратный порядок
   * не ошибка, а обычная промашка мышью, поэтому даты меняем местами сами —
   * сервер на перевёрнутый диапазон отвечает 422.
   */
  const handleDateCalendarChange = (value: Dayjs | null) => {
    if (!value) return

    const picked = value.format('YYYY-MM-DD')

    if (submittedFrom === '' || submittedTo !== '') {
      applyParams({ submittedFrom: picked, submittedTo: '', page: 0 })

      return
    }

    if (picked < submittedFrom) {
      applyParams({ submittedFrom: picked, submittedTo: submittedFrom, page: 0 })
    } else {
      applyParams({ submittedTo: picked, page: 0 })
    }

    closeDateRange()
  }

  const open = (row: ReviewQueueRow) => {
    if (row.kind === 'exercise_run') {
      navigate(ROUTES.EXERCISE_REVIEW_RUN(row.id))

      return
    }

    if (row.homework_id !== null) {
      navigate(ROUTES.HOMEWORK_ITEM_CHECK(row.student_id, row.homework_id, row.id))
    }
  }

  /**
   * Статус строки: в срезе «Все» без него не отличить проверенную работу от
   * ждущей. `checked` означает «проверено» вообще — роботом или учителем; кто
   * именно, видно по паре автобалла и действующего балла рядом. Поэтому подписи
   * свои, а не общие из `exercises.status`.
   */
  const statusChip = (row: ReviewQueueRow) => (
    <Chip
      size="small"
      variant="outlined"
      color={row.status === 'checked' ? 'success' : 'warning'}
      label={t(`exercises.review.statuses.${row.status}`)}
    />
  )

  /**
   * Действующий балл работы. Показываем его, а не только автопроверку: в срезе
   * проверенных учитель решает по баллу, стоит ли работу переоценивать, и ради
   * этого числа ему приходилось открывать каждую.
   */
  const scoreCell = (row: ReviewQueueRow) => {
    if (row.score_percent === null) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('exercises.review.noAutoScore')}
        </Typography>
      )
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          variant={row.status === 'checked' ? 'filled' : 'outlined'}
          label={`${row.score_percent}%`}
        />
        {/* Ручная оценка расходится с автопроверкой — видно, что балл переписан */}
        {row.auto_score_percent !== null && row.auto_score_percent !== row.score_percent && (
          <Typography variant="caption" color="text.secondary">
            {t('exercises.check.autoScore', { score: row.auto_score_percent })}
          </Typography>
        )}
      </Stack>
    )
  }

  /** Шапка таблицы одна и для скелетона, и для данных: разъехаться они не должны */
  const tableHead = (
    <TableHead>
      <TableRow>
        <TableCell>{t('exercises.review.student')}</TableCell>
        <TableCell>{t('exercises.review.work')}</TableCell>
        <TableCell>{t('exercises.review.source')}</TableCell>
        <TableCell>{t('exercises.review.submittedAt')}</TableCell>
        <TableCell>{t('exercises.review.score')}</TableCell>
      </TableRow>
    </TableHead>
  )

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs items={[{ label: t('exercises.review.title') }]} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('exercises.review.title')}
          </Typography>
          <PageHelpPopover pageKey="exercise-review" />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((open) => !open)}
          />
        </Box>

        {/* Все четыре фильтра — параметры GET /review-queue, так что панель не
            может отправить в API того, чего он не знает */}
        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              select
              size="small"
              label={t('exercises.review.source')}
              value={context}
              onChange={(event) => applyParams({ context: event.target.value as QueueContext, page: 0 })}
              sx={{ minWidth: 180 }}
            >
              {CONTEXTS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`exercises.review.filters.${value}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('exercises.review.stateLabel')}
              value={queueState}
              onChange={(event) => applyParams({ state: event.target.value as ReviewQueueState, page: 0 })}
              sx={{ minWidth: 200 }}
            >
              {STATES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`exercises.review.states.${value}`)}
                </MenuItem>
              ))}
            </TextField>

            <Autocomplete
              size="small"
              options={students}
              value={selectedStudent}
              onChange={(_, value) => applyParams({ studentId: value?.id ?? null, page: 0 })}
              getOptionLabel={(student) => studentOptionLabel(student)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('exercises.review.student')}
                  placeholder={t('exercises.review.anyStudent')}
                />
              )}
              sx={{ minWidth: 260 }}
            />

            <TextField
              size="small"
              label={t('exercises.review.filterPeriod')}
              value={dateRangeLabel}
              placeholder={t('exercises.review.filterPeriodPlaceholder')}
              InputProps={{ readOnly: true }}
              onClick={openDateRange}
              sx={{ minWidth: { xs: '100%', sm: 240 }, cursor: 'pointer' }}
            />

            <Popover
              open={Boolean(dateAnchorEl)}
              anchorEl={dateAnchorEl}
              onClose={closeDateRange}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                  <DateCalendar
                    value={dateCalendarValue}
                    views={['month', 'day']}
                    onChange={(value) => handleDateCalendarChange(value)}
                  />
                </LocalizationProvider>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      applyParams({ submittedFrom: '', submittedTo: '', page: 0 })
                      closeDateRange()
                    }}
                  >
                    {t('common.reset')}
                  </Button>
                  <Button size="small" variant="contained" color="secondary" onClick={closeDateRange}>
                    {t('common.ok')}
                  </Button>
                </Box>
              </Box>
            </Popover>
          </Box>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {SKELETON_ROWS.map((row) => (
                <Paper key={row}>
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="text" width="50%" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="35%" />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                {tableHead}
                <TableBody>
                  {SKELETON_ROWS.map((row) => (
                    <TableRow key={row}>
                      <TableCell>
                        <Skeleton variant="text" width="70%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={50} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : rows.length === 0 ? (
          <Paper>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              {/* Кнопки действия здесь нет специально: очередь наполняют ученики,
                  учителю нечего создавать с пустого экрана */}
              <Typography variant="body1" color="text.secondary">
                {appliedFiltersCount === 0
                  ? t('exercises.review.empty')
                  : t('exercises.review.emptyFiltered')}
              </Typography>
            </Box>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.map((row) => (
              <Paper
                key={`${row.kind}-${row.id}`}
                onClick={() => open(row)}
                sx={{ cursor: 'pointer' }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.student_name ?? '—'}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                  >
                    <span>{row.title ?? t('exercises.review.untitledWork')}</span>
                    {row.exercise_type && (
                      <Chip size="small" label={t(`exercises.types.${row.exercise_type}.name`)} />
                    )}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      color={row.context === 'homework' ? 'secondary' : 'default'}
                      label={t(`exercises.review.contexts.${row.context}`)}
                    />
                    {statusChip(row)}
                    {row.score_percent !== null && (
                      <Chip size="small" variant="outlined" label={`${row.score_percent}%`} />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t('exercises.review.submittedAt')}:{' '}
                    {row.waiting_since ? formatDateDMY(row.waiting_since) : '—'}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              {tableHead}
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={`${row.kind}-${row.id}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => open(row)}
                  >
                    <TableCell>{row.student_name ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <span>{row.title ?? t('exercises.review.untitledWork')}</span>
                        {row.exercise_type && (
                          <Chip size="small" label={t(`exercises.types.${row.exercise_type}.name`)} />
                        )}
                        {statusChip(row)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={row.context === 'homework' ? 'secondary' : 'default'}
                        label={t(`exercises.review.contexts.${row.context}`)}
                      />
                    </TableCell>
                    <TableCell>{row.waiting_since ? formatDateDMY(row.waiting_since) : '—'}</TableCell>
                    <TableCell>{scoreCell(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Пагинация живёт вне ветки с данными и вне ветки загрузки: на
            странице, где работ не осталось, она рисуется тоже — иначе со
            второй страницы, опустевшей после проверки последней работы,
            вернуться было нечем. Под скелетоном её тоже не прячем: на каждом
            перелистывании контрол исчезал и прыгал из-под курсора, а правило
            проекта прямо требует не заменять пагинацию прелоадером. Номер
            страницы показываем в пределах выдачи: адрес до неё догоняет
            эффектом выше, а MUI на страницу за пределами count ругается */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <TablePagination
            component="div"
            count={total}
            page={Math.min(page, lastPage)}
            onPageChange={(_, value) => applyParams({ page: value })}
            rowsPerPage={perPage}
            rowsPerPageOptions={[25, 50, 100]}
            onRowsPerPageChange={(event) => {
              setPerPage(parseInt(event.target.value, 10))
              applyParams({ page: 0 })
            }}
            labelRowsPerPage={isMobile ? '' : t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Box>
      </Box>
    </DashboardLayout>
  )
}
